import { Center, Group, Loader, Overlay, Paper, Radio, ScrollArea, Stack, Text } from '@mantine/core';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { pipe } from 'fp-ts/function';
import * as NEA from 'fp-ts/NonEmptyArray';
import * as O from 'fp-ts/Option';
import * as String from 'fp-ts/string';

import { useFilteredFlights } from '@/modules/booking/hooks';
import { TripStatus } from '@/modules/booking/model';
import { BVScheduledFlight } from '@/modules/common/model';

import { FlightSearchInput } from './FlightSearchInput';

type FlightWithAvailability = BVScheduledFlight & { isUnavailable: boolean };

interface Props {
  flights: Array<FlightWithAvailability>;
  loading: boolean;
  onSelect: (flight: BVScheduledFlight) => void;
  status: TripStatus;
  tripNumber: string | undefined;
}

export const FlightListing: FC<Props> = ({ flights, loading, onSelect, status, tripNumber }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | undefined>(tripNumber);
  const [search, setSearch] = useState('');
  const disabled = useMemo(() => [TripStatus.Unknown, TripStatus.Forced, TripStatus.Found].includes(status), [status]);
  const viewportRef = useRef<HTMLDivElement>(null);

  const handleChange = (value: string) => {
    setSelected(value);
    const flight = flights.find(({ number }) => number === value);
    if (flight) onSelect(flight);
  };

  const filteredFlights = useFilteredFlights(flights, search);

  useEffect(() => {
    if (disabled) setSelected('disabled'); // set undefined is not updating radio group state
  }, [disabled]);

  // Scroll to pre-selected flight once flights load (e.g. on page reload with persisted status)
  useEffect(() => {
    const viewport = viewportRef.current;

    if (!loading && tripNumber && viewport) {
      const el = viewport.querySelector<HTMLInputElement>(`input[value="${tripNumber}"]`);

      if (el) {
        const top = el.getBoundingClientRect().top - viewport.getBoundingClientRect().top;
        const middle = viewport.scrollTop + top - viewport.clientHeight / 2 + el.clientHeight / 2;
        viewport.scrollTo({ top: middle, behavior: 'smooth' });
      }
    }
  }, [tripNumber, loading]);

  return (
    <Paper shadow={disabled ? undefined : 'sm'} p={0} radius="md" withBorder pos="relative">
      <FlightSearchInput value={search} onChange={setSearch} />
      <ScrollArea
        h={250}
        scrollbarSize={4}
        viewportRef={viewportRef}
        sx={theme => ({
          borderCollapse: 'collapse',
          borderRadius: theme.radius.md,
        })}>
        {loading ? (
          <Center h={250}>
            <Loader />
          </Center>
        ) : (
          pipe(
            NEA.fromArray(filteredFlights),
            O.map(flights => (
              <Radio.Group key="flight-radio-group" value={selected} onChange={handleChange}>
                {flights.map(flight => (
                  <Radio
                    key={flight.number}
                    w="100%"
                    tt="none"
                    fw="normal"
                    value={flight.number}
                    disabled={flight.isUnavailable}
                    sx={theme => ({
                      borderBottom: '1px solid #EAEAEA',
                      ':hover': flight.isUnavailable ? undefined : { background: theme.colors.gray[0] },
                      opacity: flight.isUnavailable ? 0.5 : 1,
                    })}
                    styles={theme => ({
                      labelWrapper: { width: '100%', cursor: 'inherit' },
                      label: {
                        padding: theme.spacing.md,
                        paddingLeft: 50,
                        cursor: 'inherit',
                      },
                      body: { position: 'relative', cursor: flight.isUnavailable ? 'not-allowed' : 'pointer' },
                      inner: {
                        alignSelf: 'center',
                        position: 'absolute',
                        left: 14,
                        cursor: 'inherit',
                      },
                      radio: {
                        cursor: 'inherit',
                      },
                    })}
                    label={
                      <Group position="apart" grow>
                        <Stack spacing={5} align="start">
                          <Text fw="bold">{flight.number}</Text>
                          <Text>{flight.carrier.name}</Text>
                        </Stack>
                        <Stack spacing={5}>
                          <Text>{`${flight.departureAirport.city} (${flight.departureAirport.code})`}</Text>
                          <Text>{flight.departureTime.split(' ')[1]}</Text>
                        </Stack>
                        <Stack spacing={5}>
                          <Text>{`${flight.arrivalAirport.city} (${flight.arrivalAirport.code})`}</Text>
                          <Text>{flight.arrivalTime.split(' ')[1]}</Text>
                        </Stack>
                      </Group>
                    }
                  />
                ))}
              </Radio.Group>
            )),
            O.getOrElseW(() => (
              <Center h={250}>
                <Text>
                  {String.isEmpty(search) ? t('booking-flight_no_results') : t('booking-flight_no_search_results')}
                </Text>
              </Center>
            )),
          )
        )}
      </ScrollArea>

      {disabled && (
        <Overlay color="rgb(245, 245, 245)" opacity={0.7} radius="md" blur={1} style={{ cursor: 'not-allowed' }} />
      )}
    </Paper>
  );
};
